import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const Home: React.FC = () => {
  const { t } = useTranslation()
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isScrolled, setIsScrolled] = useState(false)

  const slides = [
    {
      title: '多用户自定义表单',
      description: '创建专业的表单，收集客户信息和订单',
      image: 'https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=modern%20form%20builder%20interface%20with%20blue%20theme%20and%20tech%20sci-fi%20elements&image_size=landscape_16_9'
    },
    {
      title: '实时订单管理',
      description: '高效处理订单，提升客户满意度',
      image: 'https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=order%20management%20dashboard%20with%20real-time%20data%20visualization&image_size=landscape_16_9'
    },
    {
      title: '产品展示系统',
      description: '创建专业的产品展示页面，吸引更多客户',
      image: 'https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=product%20catalog%20display%20with%20modern%20grid%20layout&image_size=landscape_16_9'
    }
  ]

  const features = [
    {
      icon: '📋',
      title: '表单创建',
      description: '可视化拖拽式表单编辑器，轻松创建专业表单'
    },
    {
      icon: '📊',
      title: '数据统计',
      description: '实时数据统计，了解表单提交情况'
    },
    {
      icon: '🔒',
      title: '安全可靠',
      description: '企业级安全防护，保护数据安全'
    },
    {
      icon: '🌐',
      title: '多语言支持',
      description: '全球化多语言切换，服务全球客户'
    },
    {
      icon: '📱',
      title: '响应式设计',
      description: '适配各种设备，随时随地访问'
    },
    {
      icon: '⚡',
      title: '高速性能',
      description: 'Cloudflare 全球 CDN，访问速度快'
    }
  ]

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    }, 5000)

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
      clearInterval(interval)
    }
  }, [slides.length])

  return (
    <div className="min-h-screen bg-dark text-light">
      {/* 导航栏 */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-dark/90 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <motion.div 
              initial={{ rotate: 0 }}
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="text-2xl"
            >
              📋
            </motion.div>
            <h1 className="text-xl font-bold bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text">
              FormMaster
            </h1>
          </div>
          
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-primary font-medium">{t('home')}</Link>
            <Link to="/dashboard" className="hover:text-primary transition-colors">{t('dashboard')}</Link>
            <Link to="/form-editor" className="hover:text-primary transition-colors">{t('formManagement')}</Link>
            <Link to="/order-management" className="hover:text-primary transition-colors">{t('orderManagement')}</Link>
            <Link to="/product-management" className="hover:text-primary transition-colors">{t('productManagement')}</Link>
          </div>
          
          <div className="flex items-center space-x-4">
            <Link to="/login" className="px-4 py-2 rounded-md border border-primary text-primary hover:bg-primary/10 transition-colors">
              {t('login')}
            </Link>
            <Link to="/register" className="px-4 py-2 rounded-md bg-gradient-to-r from-primary to-accent text-white hover:opacity-90 transition-opacity">
              {t('register')}
            </Link>
          </div>
        </div>
      </nav>

      {/* 轮播图 */}
      <section className="relative h-screen flex items-center overflow-hidden">
        {slides.map((slide, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: currentSlide === index ? 1 : 0 }}
            transition={{ duration: 1 }}
            className={`absolute inset-0 ${currentSlide === index ? 'block' : 'hidden'}`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-dark/80 to-dark/40 z-10"></div>
            <img 
              src={slide.image} 
              alt={slide.title} 
              className="w-full h-full object-cover"
            />
            <div className="container mx-auto px-4 relative z-20 h-full flex flex-col justify-center">
              <motion.h2 
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-4xl md:text-6xl font-bold mb-4"
              >
                {slide.title}
              </motion.h2>
              <motion.p 
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-xl md:text-2xl mb-8 max-w-2xl"
              >
                {slide.description}
              </motion.p>
              <motion.div 
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-wrap gap-4"
              >
                <Link to="/form-editor" className="px-8 py-3 rounded-md bg-gradient-to-r from-primary to-accent text-white hover:opacity-90 transition-opacity text-lg font-medium">
                  {t('createForm')}
                </Link>
                <Link to="/dashboard" className="px-8 py-3 rounded-md border border-primary text-primary hover:bg-primary/10 transition-colors text-lg font-medium">
                  {t('viewDashboard')}
                </Link>
              </motion.div>
            </div>
          </motion.div>
        ))}
        
        {/* 轮播指示器 */}
        <div className="absolute bottom-10 left-0 right-0 flex justify-center space-x-2 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${currentSlide === index ? 'bg-primary w-12' : 'bg-white/50'}`}
            />
          ))}
        </div>
      </section>

      {/* 功能模块 */}
      <section className="py-20 bg-dark">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('powerfulFeatures')}</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t('comprehensiveSolution')}
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-dark/50 backdrop-blur-sm border border-gray-800 rounded-xl p-8 hover:border-primary/50 transition-colors hover:shadow-lg hover:shadow-primary/10"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 产品展示 */}
      <section className="py-20 bg-gradient-to-b from-dark to-secondary/30">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('productShowcase')}</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t('professionalProductDisplay')}
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((item) => (
              <motion.div
                key={item}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: item * 0.1 }}
                className="bg-dark/50 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden hover:border-primary/50 transition-colors hover:shadow-lg hover:shadow-primary/10"
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={`https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=product%20${item}%20thumbnail%20with%20modern%20design&image_size=square`} 
                    alt={`Product ${item}`} 
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-2">{t('product')} {item}</h3>
                  <p className="text-gray-400 text-sm mb-4">{t('productDescription')}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-primary font-bold">¥{item * 100}</span>
                    <Link to="/product-management" className="px-4 py-2 rounded-md border border-primary text-primary hover:bg-primary/10 transition-colors text-sm">
                      {t('add_to_cart')}
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 客户评价 */}
      <section className="py-20 bg-dark">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('customerReviews')}</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t('whatOurCustomersSay')}
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((review) => (
              <motion.div
                key={review}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: review * 0.1 }}
                className="bg-dark/50 backdrop-blur-sm border border-gray-800 rounded-xl p-8 hover:border-primary/50 transition-colors hover:shadow-lg hover:shadow-primary/10"
              >
                <div className="text-yellow-400 mb-4">⭐⭐⭐⭐⭐</div>
                <p className="text-gray-300 mb-6 italic">
                  "{t('reviewText')} {review}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold mr-4">
                    {String.fromCharCode(64 + review)}
                  </div>
                  <div>
                    <h4 className="font-semibold">{t('customer')} {review}</h4>
                    <p className="text-gray-400 text-sm">{t('company')} {review}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 行动号召 */}
      <section className="py-20 bg-gradient-to-r from-primary/20 to-accent/20">
        <div className="container mx-auto px-4 text-center">
          <motion.div 
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('readyToGetStarted')}</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              {t('startBuildingFormsToday')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register" className="px-8 py-3 rounded-md bg-gradient-to-r from-primary to-accent text-white hover:opacity-90 transition-opacity text-lg font-medium">
                {t('signUpFree')}
              </Link>
              <Link to="/login" className="px-8 py-3 rounded-md border border-primary text-primary hover:bg-primary/10 transition-colors text-lg font-medium">
                {t('loginNow')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 页脚 */}
      <footer className="bg-dark border-t border-gray-800 py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="text-2xl">📋</div>
                <h3 className="text-xl font-bold bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text">
                  FormMaster
                </h3>
              </div>
              <p className="text-gray-400 mb-4">
                {t('formMasterDescription')}
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-primary transition-colors">📱</a>
                <a href="#" className="text-gray-400 hover:text-primary transition-colors">🐦</a>
                <a href="#" className="text-gray-400 hover:text-primary transition-colors">📘</a>
                <a href="#" className="text-gray-400 hover:text-primary transition-colors">📧</a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">{t('products')}</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-primary transition-colors">{t('formBuilder')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-primary transition-colors">{t('orderManagement')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-primary transition-colors">{t('productCatalog')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-primary transition-colors">{t('analytics')}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">{t('company')}</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-primary transition-colors">{t('aboutUs')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-primary transition-colors">{t('contactUs')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-primary transition-colors">{t('privacyPolicy')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-primary transition-colors">{t('termsOfService')}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">{t('support')}</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-primary transition-colors">{t('helpCenter')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-primary transition-colors">{t('documentation')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-primary transition-colors">{t('faq')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-primary transition-colors">{t('status')}</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500">
            <p>© 2026 FormMaster. {t('allRightsReserved')}</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Home
